import { useCallback } from "react";
import { Button, type ButtonProps } from "./Button";
import { Icon } from "./Icon";
import { useToast } from "./Toast";
import { shareUrl } from "@/utils/share";

interface ShareButtonProps extends Omit<ButtonProps, "onClick" | "children"> {
  /** The URL to share */
  url: string;
  /** Optional title for the share dialog */
  title?: string;
  /** Optional text description for the share dialog */
  text?: string;
  /** Show text label next to icon */
  showLabel?: boolean;
}

/**
 * ShareButton - Icon button that shares a URL via Web Share API or clipboard.
 *
 * Uses native share on supported platforms, falls back to copying to clipboard.
 */
export function ShareButton({
  url,
  title,
  text,
  showLabel = false,
  variant = "ghost-secondary",
  size = "icon-sm",
  ...props
}: ShareButtonProps) {
  const { showToast } = useToast();

  const handleShare = useCallback(async () => {
    const result = await shareUrl({ url, title, text });
    if (result.message) {
      showToast(result.message, result.success ? "success" : "error");
    }
  }, [url, title, text, showToast]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleShare}
      aria-label="Share"
      {...props}
    >
      <Icon name="arrow-up-from-bracket" size={18} />
      {showLabel && <span>Share</span>}
    </Button>
  );
}
